import * as THREE from 'three';
import { localHeadline } from '../content/headlines';
import { byBody } from '../content/looks';
import { needItem, onceToday, type GameContext } from '../content/types';
import { buzzTier } from '../state/rules';
import { STUNTS, type StuntDef } from '../systems/Stunts';
import type { GameServices } from './services';

/** "Hold my beer": picking a trampoline stunt, running it, and paying out the fame. */
export class StuntFlow {
  /** Camera focus while a stunt is running. */
  focus: THREE.Vector3 | null = null;

  constructor(private readonly s: GameServices) {}

  private cooldownKey(def: StuntDef, minutes: number): string {
    return `stunt_${def.id}_${Math.floor(minutes / 120)}`;
  }

  menu(): void {
    const { store, ui } = this.s;
    const tier = buzzTier(store.stat('buzz'));
    const courage = tier === 'good' || tier === 'buzzed';
    ui.modal.show({
      title: 'Hold My Beer',
      art: ':cartwheel:',
      text: `The trampoline creaks. The springs are 40% rust. Your phone is propped against the cooler, recording.${
        courage
          ? "\n\nYou've got a good buzz on. Liquid courage: the green zone is bigger."
          : tier === 'hammered'
            ? '\n\nYou are HAMMERED. The meter will be shaky. This is how legends (and ER visits) are made.'
            : ''
      }`,
      choices: STUNTS.map((def) => ({
        label: def.name,
        hint: `${def.presses} press${def.presses > 1 ? 'es' : ''}${def.needsBeer ? ' · -1 :beer:' : ''}`,
        requires: (ctx: GameContext) => {
          if (ctx.store.stat('energy') < 15) return 'Too tired';
          if (def.needsBeer) return needItem('beer', 1, 'beer to hold')(ctx);
          return onceToday(this.cooldownKey(def, ctx.store.minutes), 'Give the neighbors a minute')(ctx);
        },
        run: () => {
          this.begin(def);
        },
      })),
      leave: 'Maybe later',
    });
  }

  private begin(def: StuntDef): void {
    const { store, stunts } = this.s;
    const slot = this.s.home().decor.trampoline as { x: number; z: number };
    if (def.needsBeer) {
      store.take('beer');
      store.incr('beers');
      store.give('cans', 1);
    }
    store.markToday(this.cooldownKey(def, store.minutes));
    store.addStat('energy', -12);
    this.focus = new THREE.Vector3(slot.x, 1, slot.z);
    stunts.start(def, slot, store.stat('buzz'), (result) => this.result(def, result));
  }

  private result(def: StuntDef, result: 'success' | 'fail' | 'chicken'): void {
    const { store, rng, ui } = this.s;
    this.focus = null;
    const name = store.state.player.name;
    const tier = STUNTS.indexOf(def);
    if (result === 'chicken') {
      ui.modal.show({
        title: 'Chickened Out',
        art: ':chicken:',
        text: "You bounced around for a while and climbed off. The phone recorded 4 minutes of you saying 'okay okay okay.' Deleted.",
        choices: [],
        leave: 'Next time',
      });
      return;
    }
    this.s.share({ kind: 'stunt', ok: result === 'success', name: def.name });
    if (result === 'success') {
      store.incr('stunts');
      const money = [rng.int(15, 30), rng.int(45, 80), rng.int(120, 200)][tier] ?? 20;
      const clout = [4, 8, 15][tier] ?? 4;
      if (tier === 2) {
        store.unlock('viral');
        store.headline(localHeadline('stunt', name));
      }
      store.post({
        author: name,
        avatar: 'player',
        text: `${def.name.toUpperCase()} :check: first try (it was not first try) :cartwheel::fire:`,
        likes: rng.int(30, 90) * (tier + 1),
      });
      ui.modal.show({
        title: ':video-camera: VIDEO WENT VIRAL',
        art: ':fire:',
        text: `You stuck the ${def.name}! The neighbors lost their minds. Skeeter is already reposting it. Ad revenue (and a guy who "does sponsorships") paid out.`,
        choices: [],
        leave: 'Legend behavior',
      });
      store.apply({ money, clout });
      return;
    }
    store.incr('stuntFails');
    const dmg = [12, 20, 30][tier] ?? 15;
    const onTv = rng.chance(0.1);
    ui.modal.show({
      title: 'Hold My Beer: FAIL',
      art: ':ambulance:',
      text: onTv
        ? `You faceplant into the lawn. The fail video gets picked up by a home video TV show. They pay you $100 and call you "Trampoline ${byBody(store.state.player.look, 'Guy', 'Gal')}." Worth it.`
        : 'You launch off the trampoline at an angle science does not have a name for. You land in the yard. Earl rates it a 9/10 from his lawn chair.',
      choices: [],
      leave: 'Ow',
    });
    if (onTv) store.headline(localHeadline('fail', name));
    store.apply({ stats: { health: -dmg }, clout: 2, money: onTv ? 100 : 0 });
  }
}
